import { useState } from 'react'
import { MessageSquare, Plus, Trash2, Pencil, Loader2, ToggleLeft, ToggleRight, ImageIcon, X } from 'lucide-react'
import { useTelegramKeywords, useAddKeyword, useUpdateKeyword, useDeleteKeyword, useTelegramGroups, type TelegramBot, type TelegramKeywordRule } from '@/api/hooks/useTelegram'
import { toast } from 'sonner'
import { BotSelector } from './BotSelector'
import { MediaPickerModal } from './MediaPickerModal'

const emptyRule: Partial<TelegramKeywordRule> = {
  keyword: '', match_type: 'contains', action: 'dm', response_text: '',
  response_image: null, button_text: null, button_url: null,
  group_id: null, priority: 0, cooldown_minutes: 60, is_active: true,
}

export function KeywordsTab({ botId, bots, onBotChange }: { botId: string | null; bots: TelegramBot[]; onBotChange: (id: string) => void }) {
  const { data: keywords, isLoading } = useTelegramKeywords(botId)
  const { data: groups } = useTelegramGroups(botId)
  const addKeyword = useAddKeyword(botId || '')
  const updateKeyword = useUpdateKeyword()
  const deleteKeyword = useDeleteKeyword()
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<Partial<TelegramKeywordRule>>(emptyRule)
  const [mediaPicker, setMediaPicker] = useState(false)

  const openCreate = () => { setEditId(null); setForm(emptyRule); setShowModal(true) }
  const openEdit = (rule: TelegramKeywordRule) => { setEditId(rule.id); setForm(rule); setShowModal(true) }

  const handleSave = async () => {
    if (!form.keyword?.trim() || !form.response_text?.trim()) { toast.error('Keyword e resposta são obrigatórios'); return }
    try {
      if (editId) {
        await updateKeyword.mutateAsync({ id: editId, ...form })
        toast.success('Regra atualizada')
      } else {
        await addKeyword.mutateAsync(form)
        toast.success('Regra criada')
      }
      setShowModal(false)
    } catch (e: any) { toast.error(e.message) }
  }

  const handleToggle = async (rule: TelegramKeywordRule) => {
    try {
      await updateKeyword.mutateAsync({ id: rule.id, is_active: !rule.is_active })
    } catch (e: any) { toast.error(e.message) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Remover esta regra?')) return
    try { await deleteKeyword.mutateAsync(id); toast.success('Regra removida') } catch (e: any) { toast.error(e.message) }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <BotSelector bots={bots} value={botId} onChange={onBotChange} />
        {botId && <button onClick={openCreate} className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"><Plus className="h-4 w-4" /> Nova Regra</button>}
      </div>

      {!botId ? (
        <p className="text-sm text-muted-foreground">Adicione um bot primeiro na aba Bots</p>
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</div>
      ) : !keywords?.length ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center">
          <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Nenhuma regra de keyword</p>
          <p className="text-xs text-muted-foreground mt-1">Crie regras para o bot responder automaticamente quando detectar palavras-chave nos grupos</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-accent/50">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Keyword</th>
                <th className="text-left px-4 py-2 font-medium">Match</th>
                <th className="text-left px-4 py-2 font-medium">Ação</th>
                <th className="text-left px-4 py-2 font-medium">Grupo</th>
                <th className="text-left px-4 py-2 font-medium">Img</th>
                <th className="text-left px-4 py-2 font-medium">Prioridade</th>
                <th className="text-center px-4 py-2 font-medium">Ativo</th>
                <th className="text-right px-4 py-2 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {keywords.map((rule) => (
                <tr key={rule.id} className="hover:bg-accent/30">
                  <td className="px-4 py-2 font-mono text-xs">{rule.keyword}</td>
                  <td className="px-4 py-2 text-xs">{rule.match_type === 'exact' ? 'Exato' : 'Contém'}</td>
                  <td className="px-4 py-2 text-xs">{rule.action === 'dm' ? 'DM' : 'Reply'}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{rule.group?.title || 'Todos'}</td>
                  <td className="px-4 py-2">
                    {rule.response_image && <img src={rule.response_image} alt="" className="w-8 h-8 rounded object-cover" />}
                  </td>
                  <td className="px-4 py-2 text-xs">{rule.priority}</td>
                  <td className="px-4 py-2 text-center">
                    <button onClick={() => handleToggle(rule)} className="text-muted-foreground hover:text-foreground">
                      {rule.is_active ? <ToggleRight className="h-5 w-5 text-green-400" /> : <ToggleLeft className="h-5 w-5" />}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(rule)} className="p-1.5 hover:text-blue-400"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleDelete(rule.id)} className="p-1.5 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editId ? 'Editar' : 'Nova'} Regra</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-muted-foreground">Keyword *</label>
                <input type="text" value={form.keyword || ''} onChange={(e) => setForm({ ...form, keyword: e.target.value })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="palavra-chave" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground">Match</label>
                  <select value={form.match_type || 'contains'} onChange={(e) => setForm({ ...form, match_type: e.target.value as 'contains' | 'exact' })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm">
                    <option value="contains">Contém</option>
                    <option value="exact">Exato</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Ação</label>
                  <select value={form.action || 'dm'} onChange={(e) => setForm({ ...form, action: e.target.value as 'dm' | 'reply' })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm">
                    <option value="dm">DM Privada</option>
                    <option value="reply">Reply no Grupo</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Grupo</label>
                <select value={form.group_id || ''} onChange={(e) => setForm({ ...form, group_id: e.target.value || null })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm">
                  <option value="">Todos os grupos</option>
                  {groups?.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Resposta *</label>
                <textarea value={form.response_text || ''} onChange={(e) => setForm({ ...form, response_text: e.target.value })} rows={3} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="Texto da resposta (suporta HTML: <b>, <i>, <a>)" />
              </div>

              {/* Image picker */}
              <div>
                <label className="text-sm text-muted-foreground">Imagem (opcional)</label>
                {form.response_image ? (
                  <div className="mt-1 flex items-start gap-3">
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-border shrink-0">
                      <img src={form.response_image} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setForm({ ...form, response_image: null })} className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full hover:bg-black/80">
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                    <input type="text" value={form.response_image || ''} onChange={(e) => setForm({ ...form, response_image: e.target.value || null })} className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-xs text-muted-foreground" />
                  </div>
                ) : (
                  <div className="mt-1 flex gap-2">
                    <button type="button" onClick={() => setMediaPicker(true)} className="flex items-center gap-2 px-3 py-2 border border-border rounded-md text-sm hover:bg-accent transition-colors shrink-0">
                      <ImageIcon className="h-4 w-4" /> Biblioteca
                    </button>
                    <input type="text" value={form.response_image || ''} onChange={(e) => setForm({ ...form, response_image: e.target.value || null })} className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="ou cole uma URL" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground">Texto do Botão</label>
                  <input type="text" value={form.button_text || ''} onChange={(e) => setForm({ ...form, button_text: e.target.value || null })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="Saiba mais" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">URL do Botão</label>
                  <input type="text" value={form.button_url || ''} onChange={(e) => setForm({ ...form, button_url: e.target.value || null })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="https://..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground">Prioridade</label>
                  <input type="number" value={form.priority ?? 0} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Cooldown (min)</label>
                  <input type="number" value={form.cooldown_minutes ?? 60} onChange={(e) => setForm({ ...form, cooldown_minutes: parseInt(e.target.value) || 60 })} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm rounded-md border border-border hover:bg-accent">Cancelar</button>
                <button onClick={handleSave} disabled={addKeyword.isPending || updateKeyword.isPending} className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
                  {(addKeyword.isPending || updateKeyword.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <MediaPickerModal open={mediaPicker} onOpenChange={setMediaPicker} onSelect={(url) => { setForm({ ...form, response_image: url }); setMediaPicker(false) }} />
    </div>
  )
}
